import * as React from 'react';
import type {CSSProperties} from '@packages/utilities/css-properties-type';
import {styled, useTheme, type Theme} from '@packages/utilities/styled';
import {
  TabControl,
  Consumer,
  TabList,
  Tab,
} from '@packages/systems/design-system/Tabs';

const StyledTabList = styled(TabList)(({theme: {gutters}, style}) => ({
  paddingLeft: gutters.x8,
  paddingRight: gutters.x8,
  ...style,
}));

type TabsProp<TabId> = Partial<
  Record<
    // @ts-expect-error - TS2344 - Type 'TabId' does not satisfy the constraint 'string | number | symbol'.
    TabId,
    {
      label: string;
      style?: (arg1: Theme) => CSSProperties;
    }
  >
>;

type Props<TabId> =
  /**
   * Props for controlled use.
   * — Requires `value` and `onChange`.
   * — Does not need `defaultValue` because the active tab is always
   *   determined by `value`.
   */
  | {
      tabs: TabsProp<TabId>;
      value: TabId;
      onChange: (arg1: TabId) => void;
      children: (arg1: TabId) => React.ReactElement;
      defaultValue?: undefined;
      style?: CSSProperties;
    }

  /**
   * Props for uncontrolled use.
   * — Requires only `defaultValue`.
   * — Does not need `value` and `onChange`
   * — Simple use case; tab changes only when user switches tab. Use
   *   the controlled props if you need to support additional logic on
   *   how the tab might change.
   */
  | {
      tabs: TabsProp<TabId>;
      defaultValue: TabId;
      children: (arg1: TabId) => React.ReactElement;
      value?: undefined;
      onChange?: undefined;
      style?: CSSProperties;
    };

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const FlowTabsControl = <TabId extends unknown>({
  tabs,
  value,
  children,
  defaultValue,
  onChange,
  style,
}: Props<TabId>) => {
  const theme = useTheme();
  return (
    // Flow is complaining about TabId being incompatible with string
    // even though it is refined to TabId
    <TabControl
      // @ts-expect-error [PDTY-2706] - TS2322 - Type '((arg1: TabId) => void) | undefined' is not assignable to type '((id: string) => void) | undefined'.
      onChange={onChange}
      // @ts-expect-error [PDTY-2706] - TS2322 - Type 'TabId | undefined' is not assignable to type 'string | undefined'.
      activeTabId={value}
      // @ts-expect-error [PDTY-2706] - TS2322 - Type 'TabId | undefined' is not assignable to type 'string | undefined'.
      defaultActiveId={defaultValue}
    >
      <Consumer>
        {({activeTabId}) => (
          <>
            <StyledTabList style={style}>
              {Object.entries(tabs).map(
                ([id, {label, style: tabStyle}]: [any, any]) => (
                  <Tab
                    key={`flow-tabs-${id}`}
                    id={id}
                    style={tabStyle ? tabStyle(theme) : undefined}
                  >
                    {label}
                  </Tab>
                )
              )}
            </StyledTabList>
            {/* @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'TabId'. */}
            {children(activeTabId)}
          </>
        )}
      </Consumer>
    </TabControl>
  );
};
